import { useNavigate } from "@tanstack/react-router";
import { Users, Globe, Edit, Link } from "lucide-react";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { tenantCellSearch } from "@/lib/cells/cells";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EditTenantDialog } from "@/components/tenants/edit-tenant-dialog";
import { SsoSetupDialog } from "@/components/tenants/sso-setup-dialog";
import { HarmonyLogoIcon } from "@/components/icons/harmony-logo-icon";

interface TenantsGridProps {
  tenants: TenantRead[];
}

export function TenantsGrid({ tenants }: TenantsGridProps) {
  const navigate = useNavigate();

  const handleTenantClick = (tenant: TenantRead) => {
    void navigate({
      to: "/tenants/$tenantId",
      params: { tenantId: tenant.id },
      search: tenantCellSearch(tenant),
    });
  };

  return (
    <div className="grid gap-4">
      {tenants.map((tenant) => (
        <div
          key={tenant.id}
          className="hover:bg-accent/50 flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors"
          onClick={() => {
            handleTenantClick(tenant);
          }}
        >
          <div className="flex items-center space-x-4">
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={`${tenant.company} logo`}
                className="h-10 w-10 flex-shrink-0 rounded-lg border object-contain"
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border">
                <HarmonyLogoIcon className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <h4 className="font-semibold">{tenant.company}</h4>
              <div className="text-muted-foreground flex items-center space-x-2 text-sm">
                <Globe className="h-3 w-3" />
                <span>{tenant.subdomain}</span>
              </div>
              {(tenant.industry ?? tenant.size ?? tenant.subscription) && (
                <div className="mt-1 flex items-center space-x-1">
                  {tenant.industry && (
                    <Badge variant="outline" className="text-xs">
                      {tenant.industry.charAt(0).toUpperCase() +
                        tenant.industry.slice(1)}
                    </Badge>
                  )}
                  {tenant.size && (
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1 text-xs"
                    >
                      <Users className="h-3 w-3" />
                      {tenant.size}
                    </Badge>
                  )}
                  {tenant.subscription && (
                    <Badge
                      variant={
                        tenant.subscription === "pro" ? "default" : "secondary"
                      }
                      className={`text-xs ${
                        tenant.subscription === "pro"
                          ? "bg-green-600 text-white"
                          : "bg-blue-600 text-white"
                      }`}
                    >
                      {tenant.subscription === "free-tier"
                        ? "Free Tier"
                        : tenant.subscription}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className="flex items-center space-x-2"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <EditTenantDialog
                    tenant={tenant}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:cursor-pointer"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit tenant</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <SsoSetupDialog
                    tenant={tenant}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:cursor-pointer"
                      >
                        <Link className="h-3 w-3" />
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>SSO setup</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
}
